/**
 * ProfileStep Component
 *
 * Second step of the onboarding wizard.
 * Collects the user's first name and pronouns.
 */

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "../../ui/button";
import { useAuth } from "../../../hooks/useAuth";
import {
  useUpdateCurrentUser,
  useProfileOptions,
} from "../../../hooks/useCurrentUser";

interface ProfileStepProps {
  onContinue: () => void;
  isLoading: boolean;
}

export function ProfileStep({ onContinue, isLoading }: ProfileStepProps) {
  const { user: authUser } = useAuth();
  const { data: profileOptions } = useProfileOptions();
  const updateUser = useUpdateCurrentUser();

  // Pre-populate from Auth0 if available
  const [firstName, setFirstName] = useState(
    authUser?.name?.split(" ")[0] || ""
  );
  const [pronouns, setPronouns] = useState("");

  const canContinue = firstName.trim() && pronouns;

  const handleContinue = async () => {
    if (!canContinue) return;

    try {
      await updateUser.mutateAsync({
        first_name: firstName.trim(),
        pronouns,
      });
      onContinue();
    } catch {
      // Error handled by mutation state
    }
  };

  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Your Profile</h2>
        <p className="text-muted-foreground">Tell us a little about yourself</p>
      </div>

      <div className="max-w-sm mx-auto space-y-4">
        <div className="space-y-2">
          <label
            htmlFor="firstName"
            className="text-sm font-medium text-foreground"
          >
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Your first name"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="pronouns"
            className="text-sm font-medium text-foreground"
          >
            Pronouns
          </label>
          <select
            id="pronouns"
            value={pronouns}
            onChange={(e) => setPronouns(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select your pronouns</option>
            {profileOptions?.pronouns.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {updateUser.isError && (
          <p className="text-sm text-destructive">
            Something went wrong. Please try again.
          </p>
        )}
      </div>

      <div className="text-center">
        <Button
          size="lg"
          onClick={handleContinue}
          disabled={!canContinue || updateUser.isPending || isLoading}
          className="gap-2"
        >
          {updateUser.isPending || isLoading ? "Saving..." : "Continue"}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
